import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { notification, Typography } from 'antd'
import clsx from 'clsx'
import dayjs from 'dayjs'
import useDirectory from 'hooks/useDirectory'
import { Building } from 'models/Building'
import { useRecoilState, useRecoilValue } from 'recoil'
import { listPointState } from 'store/buildingStore'
import { currentPovState, povPerPointState } from 'store/krpanoStore'
import viewerLoadingState from 'store/viewerloadingState'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import { BuildingFile, Point } from 'types/building'
import Krpano from 'utils/krpano'
import { KrpanoEngine } from 'utils/KrpanoEngine'

import LazyThumbnail, { ImageType } from 'components/atoms/LazyThumbnail'
import Spinner from 'components/atoms/Spinner'
import RotatedArrowIcon from 'components/icons/RotatedArrowIcon'

type Props = {
  point: Point
}

export default function ViewerPointImages({ point }: Props) {
  const { id: buildingId } = useParams()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const isAnnotationDisplay = searchParams.get('is_display')
  const subimageId = searchParams.get('subimage')
  const { id, image360, order } = point
  const { getBuiildingType } = useDirectory()
  const [isExpanded, setIsExpanded] = useState(false)
  const currentlyRenderedImageIdRef = useRef<number | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const subImageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const viewerLoading = useRecoilValue(viewerLoadingState)
  const points = useRecoilValue(listPointState)
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [currentPov, setCurrentPov] = useRecoilState(currentPovState)
  const [, setPovPerPoint] = useRecoilState(povPerPointState)

  useEffect(() => {
    if (
      currentViewer.selectedImage &&
      currentlyRenderedImageIdRef.current === null
    ) {
      currentlyRenderedImageIdRef.current = currentViewer.selectedImage
    }
  }, [currentViewer.selectedImage])

  const { data: building } = useSWR<Building>(
    {
      url: `/v1/buildings/by-type?id=${buildingId}&building_type=${getBuiildingType()}`
    },
    {
      revalidateOnMount: false
    }
  )

  const listSubImages = useMemo(
    () =>
      currentViewer.pointDetail?.id === point.id
        ? [...(currentViewer.pointDetail?.subImages || [])].sort(
            (a, b) => Number(a.order) - Number(b.order)
          )
        : [],
    [
      point.id,
      currentViewer.pointDetail?.id,
      currentViewer.pointDetail?.subImages
    ]
  )

  const directionList = useMemo(
    () =>
      currentViewer.pointDetail?.directions
        ?.map((item) => {
          const order = points.find(
            (p) =>
              p.id !== currentViewer.selectedPoint && item.id.includes(p.id)
          )?.order
          return !order ? { ...item, target: null } : { ...item, target: order }
        })
        .filter((item: any) => item.target),
    [currentViewer.pointDetail?.directions, currentViewer.selectedPoint, points]
  )

  const handleDisplayImage = (targetFile: BuildingFile) => {
    const panoViewer = new KrpanoEngine('viewer').instance
    if (!panoViewer) return

    const pointData =
      currentViewer.pointDetail?.id === point.id
        ? currentViewer.pointDetail
        : point

    const currentScene = panoViewer.get('xml.scene')
    const matchId =
      typeof currentScene === 'string' && currentScene.startsWith('scene_')
        ? Number(currentScene.replace('scene_', ''))
        : null
    const activeImageId =
      matchId ||
      currentlyRenderedImageIdRef.current ||
      currentViewer.selectedImage ||
      pointData.image360?.id

    const frontSource = Krpano.GetFrontYaw(pointData, activeImageId)
    const frontTarget = Krpano.GetFrontYaw(pointData, targetFile.id)

    const hlookat = panoViewer.get('view.hlookat')
    const vlookat = panoViewer.get('view.vlookat')
    const fov = panoViewer.get('view.fov')

    const numH =
      hlookat !== null &&
      hlookat !== undefined &&
      !Number.isNaN(Number(hlookat))
        ? Number(hlookat)
        : null
    const numV =
      vlookat !== null &&
      vlookat !== undefined &&
      !Number.isNaN(Number(vlookat))
        ? Number(vlookat)
        : null
    const numZ =
      fov !== null && fov !== undefined && !Number.isNaN(Number(fov))
        ? Number(fov)
        : null

    const currentH = numH !== null ? numH : currentPov?.h ?? frontSource
    const currentV =
      numV !== null ? numV : currentPov?.v ?? pointData.initView?.v ?? 0
    const currentZ =
      numZ !== null ? numZ : currentPov?.z ?? pointData.initView?.z ?? 100

    const relativeRotation = currentH - frontSource
    const initH = Krpano.NormalizeAngle(frontTarget + relativeRotation)
    const initV = currentV
    const initZ = currentZ

    const sceneName = Krpano.getScenceNameByFileId(
      currentViewer.selectedPoint || point.id,
      points,
      targetFile.id
    )

    if (panoViewer.get(`scene[${sceneName}]`) === null) {
      const sceneString = Krpano.generateSceneXML({
        sceneName,
        tilesUrl: targetFile.tilesUrl || undefined,
        multires: targetFile.multires || undefined,
        s3Path: targetFile.s3Path || '',
        thumbnailUrl: targetFile.thumbnail || ''
      }).replace(/\r?\n|\r/g, ' ')

      panoViewer.set('scene_xml', sceneString)
      panoViewer.call(`includexmlstring(get(scene_xml), null, null, false)`)
    }

    currentlyRenderedImageIdRef.current = targetFile.id
    panoViewer.call(`load_scene(${sceneName},${initH},${initV},${initZ})`)

    const annotations = pointData?.annotations?.filter((item: any) => {
      if (item.imageID) {
        return item.imageID === targetFile.id
      }
      return targetFile.id === pointData.image360?.id
    })
    const stairways = pointData.stairways?.map((item: any) => ({
      ...item,
      nextFloorName: building?.floors?.find(
        (floor) => floor.id === +item.nextFloorId
      )?.name,
      areaId: undefined
    }))

    !viewerLoading.firstLoading &&
      annotations &&
      Krpano.AddAnnotation(
        panoViewer,
        annotations,
        isAnnotationDisplay,
        false,
        false
      )
    const delta = Krpano.GetSubImageDelta(pointData, targetFile.id)
    if (directionList && pointData?.directions) {
      Krpano.AddDirectionArrow(panoViewer, pointData.directions, delta)
    }
    stairways && Krpano.AddStairway(panoViewer, stairways, delta)
    Krpano.TogglePointLock(point.isLocked)
  }

  const handleSelectPoint = (
    selectedImageFile?: BuildingFile,
    pointId?: string
  ) => {
    if (pointId && currentViewer.selectedPoint !== pointId) {
      // Before leaving, snapshot current camera into point memory
      const leavingPointId = currentViewer.pointDetail?.id
      const panoViewer = new KrpanoEngine('viewer').instance
      if (leavingPointId && panoViewer) {
        const h = Number(panoViewer.get('view.hlookat'))
        const v = Number(panoViewer.get('view.vlookat'))
        const z = Number(panoViewer.get('view.fov'))
        if (!Number.isNaN(h) && !Number.isNaN(v) && !Number.isNaN(z)) {
          setPovPerPoint((prev) => ({ ...prev, [leavingPointId]: { h, v, z } }))
        }
      }
      // Reset currentPov so it won't bleed into the next point
      setCurrentPov(null)

      setIsExpanded(false)
      setCurrentViewer((prev) => ({
        ...prev,
        selectedPoint: pointId,
        selectedImage: selectedImageFile
          ? selectedImageFile.id
          : point.image360?.id,
        isClickToDirection: false,
        isSelectPointInLeftMenu: true
      }))
      return
    }

    const targetFile =
      selectedImageFile || currentViewer.pointDetail?.image360 || point.image360

    if (targetFile) {
      handleDisplayImage(targetFile)
      setCurrentViewer((prev) => ({
        ...prev,
        selectedImage: targetFile.id
      }))
    }

    // Update URL
    const urlToUpdate = new URL(window.location.href)
    urlToUpdate.searchParams.set('point', pointId || id)
    if (selectedImageFile && selectedImageFile.id !== point.image360?.id) {
      urlToUpdate.searchParams.set('subimage', selectedImageFile.id.toString())
    } else {
      urlToUpdate.searchParams.delete('subimage')
    }
    urlToUpdate.searchParams.delete('hlookat')
    urlToUpdate.searchParams.delete('vlookat')
    urlToUpdate.searchParams.delete('fov')
    window.history.pushState({}, '', urlToUpdate.toString())
  }

  useEffect(() => {
    if (
      subimageId &&
      id === currentViewer.selectedPoint &&
      point.isSubImages &&
      !isExpanded
    ) {
      setIsExpanded(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subimageId, id, currentViewer.selectedPoint])

  const handleToggle = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    if (currentViewer.selectedPoint !== point.id) {
      handleSelectPoint(undefined, point.id)
      setIsExpanded(true)
    } else {
      setIsExpanded((prev) => !prev)
    }
  }

  // Auto-collapse when another point is selected
  useEffect(() => {
    if (currentViewer.selectedPoint !== id && isExpanded) {
      setIsExpanded(false)
    }
  }, [currentViewer.selectedPoint, id, isExpanded])

  // v1.2 - 27/03/2026 - Auto expand subimages if selected image is a subimage of this point
  useEffect(() => {
    if (
      currentViewer.selectedPoint === id &&
      currentViewer.selectedImage &&
      listSubImages.some((img) => img.id === currentViewer.selectedImage)
    ) {
      setIsExpanded(true)
    }
  }, [
    id,
    currentViewer.selectedPoint,
    currentViewer.selectedImage,
    listSubImages
  ])

  useEffect(() => {
    if (currentViewer.selectedPoint === id) {
      if (currentViewer.selectedImage === image360?.id && scrollRef.current) {
        scrollRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        })
      } else if (
        isExpanded &&
        currentViewer.selectedImage &&
        subImageRefs.current[currentViewer.selectedImage]
      ) {
        subImageRefs.current[currentViewer.selectedImage]?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        })
      }
    }
  }, [
    currentViewer.selectedPoint,
    currentViewer.selectedImage,
    id,
    isExpanded,
    image360?.id
  ])

  return (
    <>
      <div
        ref={scrollRef}
        className={clsx('p-[10px] min-h-[36px] relative', {
          'theme-bg-sub-primary':
            currentViewer.selectedPoint === id &&
            currentViewer.selectedImage === image360?.id
        })}
        onClick={() => handleSelectPoint(undefined, point.id)}
      >
        <div
          className={`absolute ${
            image360 ? 'top-[15px] left-[16px]' : 'top-[10px] left-[10px]'
          } z-10 rounded-lg flex bg-[#021120] p-[2px] min-w-[16px]`}
        >
          <div className="m-auto text-[13px] leading-[13px] font-bold text-white">
            {order}
          </div>
        </div>

        {point.isSubImages && (
          <div
            className="absolute top-[15px] left-[35px] z-10 rounded-full flex bg-[#787B82CC] w-[18px] h-[18px]"
            onClick={handleToggle}
          >
            <RotatedArrowIcon className="m-auto" />
          </div>
        )}

        {image360 && (
          <>
            <LazyThumbnail
              imageType={ImageType.OBJECT_FILL}
              url={
                image360.multires
                  ? `${image360.tilesUrl}thumb.jpg`
                  : image360.thumbnail
              }
              name={image360.name}
            />
            {image360 && !image360?.note ? (
              <div className="ant-typography text-white text-[12px] bg-[#02112070] absolute right-[10px] bottom-[10px] px-1 leading-none flex items-center">
                <Typography.Text className="text-[12px] text-white">
                  {dayjs(image360?.takenAt)
                    ?.utc()
                    .format('YYYY-MM-DD HH:mm')}
                </Typography.Text>
              </div>
            ) : (
              <div className="ant-typography text-white text-[12px] max-w-[100px] bg-[#02112070] absolute right-[10px] bottom-[10px] px-1 leading-none flex items-center">
                <Typography.Text
                  className="text-[12px] text-white truncate"
                  ellipsis={{ tooltip: image360?.note }}
                >
                  {image360?.note}
                </Typography.Text>
              </div>
            )}
          </>
        )}
      </div>
      {isExpanded &&
        listSubImages.map((item) => (
          <div
            key={item.id}
            ref={(el) => {
              subImageRefs.current[item.id] = el
            }}
            className={clsx('p-[10px] min-h-[36px] relative', {
              'theme-bg-sub-primary': currentViewer.selectedImage === item.id
            })}
            onClick={() => {
              if (item.needsThumbnail) {
                notification.info({
                  message: t(
                    'building.generatingNewThumbnail',
                    '新しいサムネイルを生成中です'
                  )
                })
              }
              handleSelectPoint(item, point.id)
            }}
          >
            <div className="relative overflow-hidden">
              <LazyThumbnail
                imageType={ImageType.OBJECT_FILL}
                url={
                  item.thumbnailUrl ||
                  (item.multires ? `${item.tilesUrl}thumb.jpg` : item.thumbnail)
                }
                name={item.name}
              />
              {item.needsThumbnail && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#00000050] z-10">
                  <Spinner size="small" />
                </div>
              )}
            </div>
            {item && !item?.note ? (
              <div className="ant-typography text-white text-[12px] bg-[#02112070] absolute right-[10px] bottom-[10px] px-1 leading-none flex items-center">
                <Typography.Text className="text-[12px] text-white">
                  {dayjs(item?.takenAt)
                    ?.utc()
                    .format('YYYY-MM-DD HH:mm')}
                </Typography.Text>
              </div>
            ) : (
              <div className="ant-typography text-white text-[12px] max-w-[180px] bg-[#02112070] absolute right-[10px] bottom-[10px] px-1 leading-none flex items-center">
                <Typography.Text
                  className="text-[12px] text-white truncate"
                  ellipsis={{ tooltip: item?.note }}
                >
                  {item?.note}
                </Typography.Text>
              </div>
            )}
          </div>
        ))}
    </>
  )
}
